/**
 * Shipping addresses — same table as zoolyfe.id (alamat_pengiriman).
 */
import { makeAuthenticatedApiCall } from './authApi';
import { USER_API_URL } from '../config/api';

/**
 * @returns {Promise<Array<object>>}
 */
export const getShippingAddresses = async () => {
  const url = `${USER_API_URL}/shipping-addresses`;
  const res = await makeAuthenticatedApiCall(url, { method: 'GET' });
  if (res?.status !== 'success') throw new Error(res?.message || 'Gagal memuat alamat');
  return Array.isArray(res?.data) ? res.data : [];
};

/**
 * @param {number} id
 * @returns {Promise<object|null>}
 */
export const getShippingAddress = async (id) => {
  const url = `${USER_API_URL}/shipping-addresses/${id}`;
  const res = await makeAuthenticatedApiCall(url, { method: 'GET' });
  if (res?.status !== 'success') throw new Error(res?.message || 'Gagal memuat alamat');
  return res?.data?.address ?? null;
};

/**
 * @param {string} q
 * @param {number} [limit]
 * @returns {Promise<Array<{ kode: string, label: string, nama_kecamatan: string, nama_kota: string, nama_provinsi: string, kodepos: string }>>}
 */
export const searchShippingAreas = async (q, limit = 25) => {
  const url = `${USER_API_URL}/shipping-areas/search?q=${encodeURIComponent(q)}&limit=${limit}`;
  const res = await makeAuthenticatedApiCall(url, { method: 'GET' });
  if (res?.status !== 'success') throw new Error(res?.message || 'Gagal mencari wilayah');
  return res?.data?.areas ?? [];
};

export const listShippingProvinces = async () => {
  const url = `${USER_API_URL}/shipping-areas/provinces`;
  const res = await makeAuthenticatedApiCall(url, { method: 'GET' });
  if (res?.status !== 'success') throw new Error(res?.message || 'Gagal memuat provinsi');
  return Array.isArray(res?.data?.provinces) ? res.data.provinces : [];
};

export const listShippingCities = async (kodeProvinsi) => {
  const url = `${USER_API_URL}/shipping-areas/cities?kode=${encodeURIComponent(kodeProvinsi || '')}`;
  const res = await makeAuthenticatedApiCall(url, { method: 'GET' });
  if (res?.status !== 'success') throw new Error(res?.message || 'Gagal memuat kota');
  return Array.isArray(res?.data?.cities) ? res.data.cities : [];
};

export const listShippingPostalCodes = async (kodeKota) => {
  const url = `${USER_API_URL}/shipping-areas/postal?kode=${encodeURIComponent(kodeKota || '')}`;
  const res = await makeAuthenticatedApiCall(url, { method: 'GET' });
  if (res?.status !== 'success') throw new Error(res?.message || 'Gagal memuat kode pos');
  return Array.isArray(res?.data?.postal) ? res.data.postal : [];
};

/**
 * @param {object} payload
 */
export const createShippingAddress = async (payload) => {
  const url = `${USER_API_URL}/shipping-addresses`;
  const res = await makeAuthenticatedApiCall(url, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (res?.status !== 'success') throw new Error(res?.message || 'Gagal menyimpan alamat');
  return res?.data?.address ?? null;
};

/**
 * @param {number} id
 * @param {object} payload
 */
export const updateShippingAddress = async (id, payload) => {
  const url = `${USER_API_URL}/shipping-addresses/${id}`;
  const res = await makeAuthenticatedApiCall(url, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  if (res?.status !== 'success') throw new Error(res?.message || 'Gagal memperbarui alamat');
  const saved = res?.data?.address ?? null;
  if (saved?.nama_penerima || saved?.detail_alamat || saved?.nama_alamat) {
    return saved;
  }
  return getShippingAddress(id);
};

/**
 * @param {number} id
 */
export const deleteShippingAddress = async (id) => {
  const url = `${USER_API_URL}/shipping-addresses/${id}`;
  const res = await makeAuthenticatedApiCall(url, { method: 'DELETE' });
  if (res?.status !== 'success') throw new Error(res?.message || 'Gagal menghapus alamat');
};

/**
 * @param {number} id
 */
export const setPrimaryShippingAddress = async (id) => {
  const url = `${USER_API_URL}/shipping-addresses/${id}/set-primary`;
  const res = await makeAuthenticatedApiCall(url, {
    method: 'PATCH',
    body: JSON.stringify({}),
  });
  if (res?.status !== 'success') throw new Error(res?.message || 'Gagal mengatur alamat utama');
  return res?.data?.address ?? null;
};
